import classnames from 'classnames';
import * as React from 'react';

import { Button, Grid, GridItem, Icon } from '@ao-internal/components-react';

import useChat from '../hooks/useChat';
import { InputTypes } from '../store';
import {
	ActivateLiveChatContentType,
	LiveChatAgent,
	LiveChatStatus,
	SenderTypes,
} from '../store';

export interface LiveChatStatusBarProps
	extends React.HTMLProps<HTMLDivElement> {
	status?: LiveChatStatus;
	agent?: LiveChatAgent;
	liveChatActivationData?: ActivateLiveChatContentType;
	showAutoDisconnectNotice?: boolean;
	isReadOnly?: boolean;
}

export const LiveChatStatusBar: React.FC<LiveChatStatusBarProps> = ({
	status,
	agent,
	liveChatActivationData,
	showAutoDisconnectNotice,
	isReadOnly,
	className,
}) => {
	const { exitLiveChat, startLiveChat, sendMessage } = useChat();

	function getStatusString() {
		if (agent && status === LiveChatStatus.Connected) {
			return 'Active now';
		}
		if (status === LiveChatStatus.Disconnected) {
			return 'Disconnected';
		}
		if (status === LiveChatStatus.Transferring) {
			return 'Transferring...';
		}
		return 'Connecting...';
	}

	function handleReconnect() {
		if (liveChatActivationData) {
			startLiveChat(liveChatActivationData);
		}
	}

	function handleCancel() {
		exitLiveChat(SenderTypes.User);
	}

	function handleDismissAutoDisconnectClick() {
		sendMessage("I'm still here", {
			inputType: InputTypes.Interaction,
		});
	}

	if (status === LiveChatStatus.ConnectionError) {
		return (
			<div
				ref={(node) => node && isReadOnly && node.setAttribute('inert', '')}
				data-testid="live-chat-status-bar-connecting-error"
				className={classnames(
					'shadow-4dp bg-white pt-2 pb-4 px-3 text-body-sm border-b',
					className
				)}
			>
				<div className="bg-error p-4 m-2 rounded">
					<div className="flex items-start">
						<div className="flex items-center">
							&#8203;
							<Icon className="block text-black" icon="warning" size="2x" />
						</div>
						<div className="flex flex-col flex-grow pl-2">
							<strong className="text-body block pb-1">Connection error</strong>
							<span className="text-body-sm mb-2">
								Sorry, there’s been a problem connecting you to an agent.
							</span>

							<Grid>
								<GridItem span={6}>
									<Button
										variant="secondary"
										className="justify-center w-full text-center"
										onClick={handleReconnect}
									>
										Reconnect
									</Button>
								</GridItem>
								<GridItem span={6}>
									<Button
										variant="secondary"
										className="justify-center w-full text-center"
										onClick={handleCancel}
									>
										Cancel
									</Button>
								</GridItem>
							</Grid>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			ref={(node) => node && isReadOnly && node.setAttribute('inert', '')}
			data-testid="live-chat-status-bar"
			className={classnames(
				'bg-white border-b border-t-0 border-l-0 border-r-0 border-solid border-gray-40 shadow-none',
				className
			)}
		>
			<div className="text-caption flex px-3 py-2">
				<div className="flex items-center">
					<span
						className={classnames('mr-2 rounded block', {
							'bg-ongreen': status === LiveChatStatus.Connected,
							'bg-alert':
								status === LiveChatStatus.Connecting ||
								status === LiveChatStatus.Transferring,
							'bg-error': status === LiveChatStatus.Disconnected,
						})}
						style={{ width: '10px', height: '10px' }}
					></span>
					{agent && status !== LiveChatStatus.Transferring ? (
						<React.Fragment>
							<span className="font-bold">
								{agent.name.split(' ')[0]}&nbsp;&nbsp;
							</span>
							{getStatusString()}
						</React.Fragment>
					) : (
						<span className="font-bold">{getStatusString()}</span>
					)}
				</div>
				{status !== LiveChatStatus.Disconnected && (
					<div className="ml-auto">
						<button
							data-tag-name="disconnect livechat cta"
							type="button"
							className="text-link p-0 text-xs bg-white cursor-pointer"
							onClick={() => exitLiveChat(SenderTypes.User)}
						>
							Disconnect
						</button>
					</div>
				)}
			</div>
			{showAutoDisconnectNotice && (
				<div
					data-testid="live-chat-auto-disconnect-notice"
					className="px-3 pt-1 pb-3"
				>
					<div className="bg-lightblue flex items-start p-3 text-base border border-0 rounded-lg">
						<div className="flex items-center pr-2">
							<span
								className="ico ico-notification text-placeholder block"
								role="img"
								style={{ fontSize: '22px' }}
							></span>
						</div>
						<span className="flex-grow break-words">
							<strong className="text-body block mb-2">
								Are you still there?
							</strong>
							<p className="text-body-sm">
								If we don&apos;t hear from you in the next 5 minutes, I&apos;m
								afraid we&apos;ll have to disconnect live chat.<br></br>
								<button
									data-tag-name="dismiss auto disconnect"
									type="button"
									className="cta cta-secondary mt-2 cursor-pointer"
									onClick={handleDismissAutoDisconnectClick}
								>
									I&apos;m still here
								</button>
							</p>
						</span>
					</div>
				</div>
			)}
		</div>
	);
};

export default LiveChatStatusBar;
